import { EditIcon } from '@persona-id/design-tokens/icons';
import { camelCase, kebabCase, startCase } from 'lodash/fp';
import { useMemo, VFC } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { ListStatus } from 'dashboard/models/list';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionRemoveItemFromListV1 } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const FieldValue = styled.div`
  font-size: 0.9em;
`;

const FieldLabel = styled.div`
  font-weight: bolder;
  font-size: 0.9em;
`;

type SupportedListType =
  | ApiModel.ListBrowserFingerprint
  | ApiModel.ListDeviceFingerprint
  | ApiModel.ListCountry
  | ApiModel.ListEmailAddress
  | ApiModel.ListField
  | ApiModel.ListGovernmentIdNumber
  | ApiModel.ListIpAddress
  | ApiModel.ListName
  | ApiModel.ListPhoneNumber
  | ApiModel.ListString;

const ListItemFields: Record<SupportedListType, string[]> = {
  [ApiModel.ListBrowserFingerprint]: ['browser-fingerprint'],
  [ApiModel.ListDeviceFingerprint]: ['device-fingerprint'],
  [ApiModel.ListCountry]: ['country-code'],
  [ApiModel.ListEmailAddress]: ['email-address'],
  [ApiModel.ListField]: ['field'],
  [ApiModel.ListGovernmentIdNumber]: ['id-class', 'id-number'],
  [ApiModel.ListIpAddress]: ['ip-address'],
  [ApiModel.ListName]: ['name-first', 'name-last'],
  [ApiModel.ListPhoneNumber]: ['phone-number'],
  [ApiModel.ListString]: ['string'],
};

interface ParamsRemoveItemFromListV1Props extends ParamsBaseProps {
  lists: WorkflowActionMeta['lists'];
  editable: boolean;
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
  params: WorkflowActionRemoveItemFromListV1['params'];
  updateActionParams: (newParams: WorkflowActionRemoveItemFromListV1['params']) => void;
}

const ParamsRemoveItemFromListV1: VFC<ParamsRemoveItemFromListV1Props> = ({
  lists,
  editable,
  errors,
  usableObjects,
  params,
  updateActionParams,
  updateStep,
  step,
}) => {
  const hasListPerm = usePermission(Permission.ListView);
  const isTemplateAvailable = useMemo(() => {
    return !params['list-id'] || lists.map((list) => list.id).includes(params['list-id']);
  }, [lists, params]);
  const availableLists = useMemo(() => {
    return (
      lists
        ?.filter(
          (list) =>
            list.attributes['status'] === ListStatus.Active &&
            ListItemFields[list.type as SupportedListType],
        )
        .map((list) => ({
          label: list.name,
          value: list.id,
        })) || []
    );
  }, [lists]);

  const updateList = (listId: WorkflowActionRemoveItemFromListV1['params']['list-id']) => {
    const listName = availableLists.find((l) => l.value === listId)?.label;
    updateStep({
      ...step,
      name: `Remove from List: ${listName}`,
      action: {
        ...(step.action as WorkflowActionRemoveItemFromListV1),
        params: {
          ...params,
          'list-id': listId,
          'list-item-fields': {},
        },
      },
    });
  };

  const updateListItemFields = (value: Record<string, string>) => {
    updateActionParams({
      ...params,
      'list-item-fields': Object.keys(value).reduce<Record<string, string>>(
        (acc, elem) => ({
          ...acc,
          [elem]: value[elem],
        }),
        {},
      ),
    });
  };

  const [showFieldMappingsPanel, { toggle: toggleFieldMappingsPanel }] = useToggle(false);

  const fieldOptions = (
    ListItemFields[lists.find((l) => l.id === params['list-id'])?.type as SupportedListType] || []
  ).reduce<Record<string, string>>(
    (acc, field) => ({
      ...acc,
      [kebabCase(field)]: field,
    }),
    {},
  );

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionRemoveItemFromListV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='List'
        htmlFor='params-remove-item-to-list__list-input'
        helpText={
          hasListPerm
            ? isTemplateAvailable
              ? undefined
              : 'The current list is not available to you at this time.'
            : 'Your role does not have permissions to view lists.'
        }
      />
      <ReactSelect
        value={params['list-id']}
        options={availableLists}
        onChange={updateList}
        disabled={!editable || !hasListPerm}
        placeholder={hasListPerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'}
        hasError={hasErrorFor('list-id')}
        id='params-remove-item-to-list__list'
      />
      {renderErrorMessageFor('list-id')}
      <Spacer height={2} />
      {editable && isTemplateAvailable && Object.keys(fieldOptions).length > 0 && (
        <>
          <ParamLabel
            labelText='Item Fields'
            htmlFor='params-remove-item-to-list__remove-item'
            buttons={[
              <Button
                key='edit'
                width='icon'
                variant='basic'
                onClick={toggleFieldMappingsPanel}
                id='params-remove-item-to-list__remove-item'
              >
                <EditIcon />
              </Button>,
            ]}
          />
          {renderErrorMessageFor('list-item-fields')}
          {showFieldMappingsPanel && (
            <FieldMappingsPanel
              title='Item Fields'
              usableObjects={usableObjects}
              fieldMappings={params['list-item-fields'] ?? {}}
              presetFields={fieldOptions}
              onOutsideClick={toggleFieldMappingsPanel}
              onUpdateFieldMappings={updateListItemFields}
            />
          )}
        </>
      )}
      <Spacer height={1} />
      {isTemplateAvailable &&
        Object.entries(params['list-item-fields'] || {}).map(([key, value]) => (
          <div key={key}>
            <FieldLabel>{startCase(camelCase(key))}</FieldLabel>
            <FieldValue>{value}</FieldValue>
          </div>
        ))}
    </>
  );
};

export default ParamsRemoveItemFromListV1;
